"use client";

import * as React from "react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Link } from "@/components/common/Link";
import Autoplay from "embla-carousel-autoplay";
const caseStudies = [{
  id: 1,
  title: "製造業A社のベトナム工場設立",
  description: "日本の大手製造業が、ベトナムに生産拠点を開設。投資許可取得から操業開始まで、全プロセスを支援しました。",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1411237/img/manufacturing_factory_vietnam.png"
}, {
  id: 2,
  title: "小売業B社の市場参入戦略",
  description: "消費財の輸入販売を目的とした現地法人設立をサポート。市場調査から流通網構築まで一貫してサポートしました。",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1411237/img/retail_market_entry_vietnam.png"
}, {
  id: 3,
  title: "IT企業C社のオフショア開発拠点",
  description: "ソフトウェア開発の拠点を設立。優秀なエンジニアの採用から、オフィス開設、法務対応まで支援しました。",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1411237/img/it_offshore_development_center.png"
}];
function PostsGridConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="rounded-xl bg-muted/30 animate-pulse overflow-hidden">
            <div className="aspect-[16/10] bg-muted/50" />
            <div className="p-6 space-y-3">
              <div className="h-6 bg-muted/50 rounded w-3/4" />
              <div className="h-4 bg-muted/50 rounded w-full" />
              <div className="h-4 bg-muted/50 rounded w-5/6" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-12">
        <p className="text-foreground-muted mb-4">
          投稿の読み込みに失敗しました。
        </p>
        <button onClick={refetch} className="inline-flex items-center justify-center rounded-md bg-primary text-foreground-primary px-6 py-3 font-semibold hover:bg-primary-hover hover:shadow-medium hover:-translate-y-0.5 active:bg-primary-active active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)]">
          再試行
        </button>
      </div>;
  }
  if (posts.length === 0) {
    return <p className="text-center text-foreground-muted py-12">
        表示する投稿がありません。
      </p>;
  }
  return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, i) => <article key={post.id} data-index={i} className="group">
          <Card className="h-full overflow-hidden border border-border shadow-soft hover:shadow-medium transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] hover:-translate-y-1 bg-background">
            {post.featuredImage && <div className="relative overflow-hidden aspect-[16/10]">
                <img src={post.featuredImage} alt={post.title} className="w-full h-full object-cover transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] group-hover:scale-105" />
                <div className="absolute inset-0 bg-primary-dark/40 opacity-0 group-hover:opacity-100 transition-opacity duration-[var(--duration-medium)] ease-[var(--ease-smooth)]" />
              </div>}
            <CardHeader>
              <CardTitle className="text-xl text-primary group-hover:text-accent-dark transition-colors duration-[var(--duration-medium)] line-clamp-2">
                {post.title}
              </CardTitle>
              {post.date && <CardDescription className="text-sm text-foreground-muted">
                  {new Date(post.date).toLocaleDateString("ja-JP", {
              year: "numeric",
              month: "long",
              day: "numeric"
            })}
                </CardDescription>}
            </CardHeader>
            {post.excerpt && <CardContent>
                <div className="text-sm text-foreground-muted line-clamp-3 leading-relaxed" dangerouslySetInnerHTML={{
            __html: post.excerpt
          }} />
              </CardContent>}
            {post.link && <CardContent className="pt-0">
                <Link to={post.link} className="inline-flex items-center text-sm font-semibold text-primary hover:text-accent-dark transition-colors duration-[var(--duration-medium)] group-hover:translate-x-1 transition-transform">
                  続きを読む
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-1">
                    <path d="M5 12h14" />
                    <path d="m12 5 7 7-7 7" />
                  </svg>
                </Link>
              </CardContent>}
          </Card>
        </article>)}
    </div>;
}
export default function SeikoJirei() {
  const autoplayRef = React.useRef(Autoplay({
    delay: 4000,
    stopOnInteraction: true,
    stopOnMouseEnter: true
  }));
  return <>
      {}
      <section className="relative py-24 bg-light-background overflow-hidden">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-primary mb-4">
              最新ニュース
            </h2>
            <p className="text-lg text-foreground-muted max-w-2xl mx-auto">
              ベトナム進出に関する最新情報やビジネスインサイトをお届けします。
            </p>
          </div>

          <div>
            <PostsProvider wp_query={{
            per_page: 6,
            order: "desc",
            orderby: "date"
          }} embeds={["wp:featuredmedia", "wp:term"]} fields={["id", "title.rendered", "excerpt.rendered", "link", "date", "_embedded", "_links.wp:featuredmedia", "_links.wp:term"]}>
              <PostsGridConsumer />
            </PostsProvider>
          </div>
        </div>
      </section>

      {}
      <section className="relative py-24 bg-light-background overflow-hidden">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-primary mb-4">
              成功事例
            </h2>
            <p className="text-lg text-foreground-muted max-w-2xl mx-auto">
              日本企業のベトナム進出を支援してきた実績をご紹介します。
            </p>
          </div>

          <div>
            <Carousel opts={{
            align: "start",
            loop: true
          }} plugins={[autoplayRef.current]} className="w-full max-w-6xl mx-auto">
              <CarouselContent>
                {caseStudies.map((study, i) => <CarouselItem key={study.id} data-index={i} className="md:basis-1/2 lg:basis-1/3">
                    <div className="p-2">
                      <Card className="group overflow-hidden border border-border shadow-soft hover:shadow-medium transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] hover:-translate-y-1">
                        <div className="relative overflow-hidden aspect-[4/3]">
                          <img src={study.image} alt={study.title} className="w-full h-full object-cover transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] group-hover:scale-105" />
                          <div className="absolute inset-0 bg-primary-dark/60 opacity-0 group-hover:opacity-100 transition-opacity duration-[var(--duration-medium)] ease-[var(--ease-smooth)]" />
                          <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-full group-hover:translate-y-0 transition-transform duration-[var(--duration-medium)] ease-[var(--ease-spring)]">
                            <p className="text-foreground-dark text-sm leading-relaxed">
                              {study.description}
                            </p>
                          </div>
                        </div>
                        <CardHeader>
                          <CardTitle className="text-xl text-primary group-hover:text-accent-dark transition-colors duration-[var(--duration-medium)]">
                            {study.title}
                          </CardTitle>
                        </CardHeader>
                      </Card>
                    </div>
                  </CarouselItem>)}
              </CarouselContent>
              <CarouselPrevious className="bg-background text-primary border-border hover:bg-primary hover:text-foreground-primary -left-12 shadow-soft" />
              <CarouselNext className="bg-background text-primary border-border hover:bg-primary hover:text-foreground-primary -right-12 shadow-soft" />
            </Carousel>
          </div>
        </div>
      </section>
    </>;
}